// The season wrapped (scenario H9, PLAN-2026-10-18-families-and-newcomers
// §12.1): a team's season told as a few cards — the finish, the best week,
// the awards, the pickup, the rivalry, pick'em and the league — ending in
// one image to share. The cards are the server's (`SeasonWrapView`); this
// screen only lays them out.
//
// TOLD AS THE RECAP IS (B-405): cards that swipe on a phone, a page on a
// wider screen, and either switches. `?team=` reads another team's; mine by
// default. Drawn through the kit (LOOKS.md Part 7).

import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SeasonWrapView, WrapCardView } from '@fsp/domain';
import { useLeagueBase, useMyTeamId, useSeasonWrap } from '../data/hooks';
import { TeamMark } from '../kit';
import { Button, Caption, Cover, ListRow, RowName, RowSub, Segments, Text, Title } from '../kit/ui';
import { CardRow } from '../kit/cards';
import { useRemembered } from '../shell/tabMemory';
import { useTier } from '../shell/useTier';
import { AwardIcon, SharePanel } from './shared/AwardKit';
import { FromLeague } from './shared/FromLeague';
import '../styles/screens/waivers.css';
import '../styles/screens/league-screens.css';
import '../styles/screens/home.css';

/**
 * THE SAME AWARD ONCE, WITH ITS WEEKS (the walk of 2026-10-19): a champion's
 * fourteen weekly awards were fourteen rows, most of them one award again.
 * The season's awards first, then the weekly ones by how often.
 */
function grouped(awards: WrapCardView['awards']) {
  const by = new Map<
    string,
    {
      key: string;
      name: string;
      icon: WrapCardView['awards'][number]['icon'];
      tone: WrapCardView['awards'][number]['tone'];
      season: boolean;
      weeks: number;
    }
  >();
  for (const a of awards) {
    const season = a.period === null;
    const key = `${season ? 'season' : 'week'}:${a.awardId}`;
    const row = by.get(key) ?? { key, name: a.name, icon: a.icon, tone: a.tone, season, weeks: 0 };
    row.weeks += 1;
    by.set(key, row);
  }
  return [...by.values()].sort(
    (x, y) =>
      Number(y.season) - Number(x.season) || y.weeks - x.weeks || x.name.localeCompare(y.name),
  );
}

export function SeasonWrapScreen() {
  const [params] = useSearchParams();
  const mine = useMyTeamId();
  const teamId = params.get('team') ?? mine;
  return (
    <FromLeague snapshot={useSeasonWrap(teamId)}>{(wrap) => <WrapBody wrap={wrap} />}</FromLeague>
  );
}

function WrapBody({ wrap }: { wrap: SeasonWrapView }) {
  const base = useLeagueBase();
  const phone = useTier() === 'phone';
  const [chosen, setChosen] = useRemembered<'story' | 'page' | null>('wrap:view', null);
  const view = chosen ?? (phone ? 'story' : 'page');
  const title = wrap.team.isMine ? 'Your season' : `${wrap.team.name}'s season`;

  const header = (
    <div className="fsp-waivers-head">
      <div className="fsp-waivers-head-body">
        <Title>{title}</Title>
        <div className="fsp-sub fsp-ui-wrap-text">
          {wrap.league.name} · {wrap.season}
        </div>
      </div>
      {wrap.over ? (
        <Segments
          label="Read it as"
          items={[
            { id: 'story', label: 'Story' },
            { id: 'page', label: 'Page' },
          ]}
          value={view}
          onChange={(v) => setChosen(v as 'story' | 'page')}
        />
      ) : null}
    </div>
  );

  /** One card's body, the same words in the story and on the page. */
  const body = (c: WrapCardView): ReactNode => (
    <>
      <Caption>{c.title}</Caption>
      {c.value ? <div className="fsp-disp fsp-wrap-value">{c.value}</div> : null}
      <div className={`fsp-wrap-line ${c.matchupId ? 'fsp-row-clickable fsp-cover-host' : ''}`}>
        {c.matchupId ? <Cover label="Open the game" to={`${base}/matchup/${c.matchupId}`} /> : null}
        {c.team ? <TeamMark team={c.team} size={22} /> : null}
        <Text>{c.line}</Text>
      </div>
      {/* THE YEARBOOK (H9): the league's own story, once the card names
          its champion. */}
      {c.id === 'league' && c.team ? (
        <div>
          <Button size="sm" icon="yearbook" to={`${base}/yearbook`}>
            Open the yearbook
          </Button>
        </div>
      ) : null}
      {c.awards.length > 0 ? (
        <div className="fsp-surface fsp-wrap-awards">
          {grouped(c.awards).map((a) => (
            <ListRow
              key={a.key}
              leadSize="md"
              lead={
                <span className="fsp-award-icon fsp-chat-card-glyph" data-tone={a.tone}>
                  <AwardIcon icon={a.icon} size={13} />
                </span>
              }
            >
              <RowName>{a.name}</RowName>
              <RowSub>
                {a.season ? 'The season' : a.weeks === 1 ? 'One week' : `${a.weeks} weeks`}
              </RowSub>
            </ListRow>
          ))}
        </div>
      ) : null}
    </>
  );

  const share = wrap.share ? (
    <SharePanel
      input={{ ...wrap.share, footer: `${wrap.league.name} · ${wrap.season}`, team: wrap.team }}
      text={`${wrap.share.headline}, ${wrap.season}: ${wrap.share.subline}. ${wrap.share.line}`}
    />
  ) : null;

  const story = [
    ...wrap.cards.map((c) => ({
      key: c.id,
      content: <div className="fsp-surface fsp-story-card">{body(c)}</div>,
    })),
    ...(share
      ? [{ key: 'share', content: <div className="fsp-surface fsp-story-card">{share}</div> }]
      : []),
  ];

  return (
    <div className="fsp-screen fsp-flow">
      <div className="fsp-pinned">{header}</div>
      <div className="fsp-regions fsp-board-one">
        <section className="fsp-region fsp-region-plain" aria-label={title}>
          {!wrap.over ? (
            <div className="fsp-empty">
              {wrap.team.isMine
                ? 'Your season is still going. It is told here once your run at the title is over.'
                : `${wrap.team.name}'s season is still going. It is told here once their run at the title is over.`}
            </div>
          ) : view === 'story' ? (
            <div className="fsp-recap fsp-recap-story" data-phone={phone || undefined}>
              <CardRow label={title} what="page" items={story} />
            </div>
          ) : (
            <article className="fsp-recap fsp-wrap-page" data-phone={phone || undefined}>
              {wrap.cards.map((c) => (
                <section key={c.id} className="fsp-surface fsp-wrap-card" aria-label={c.title}>
                  {body(c)}
                </section>
              ))}
              {share ? <section className="fsp-surface fsp-wrap-card">{share}</section> : null}
            </article>
          )}
        </section>
      </div>
    </div>
  );
}
